import { type FC, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Box,
  Grid,
  Divider,
  Typography,
  Button,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useUser,
  useSignOut,
  UpdateCurrentUserMutation,
} from "@/partner/entities/auth";
import { FormBuilder } from "@/shared/components/Form";
import { getTimezoneOptions } from "@/shared/utils/timezones";
import { getUserSettingsSchema, mapUserToFormValues } from "../model";
import { reloadWithoutPrompt } from "@/shared/utils/reloadWithoutPrompt";

export const UserSettings: FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [signOut] = useSignOut();

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const schema = useMemo(
    () => getUserSettingsSchema(t, timezoneOptions),
    [t, timezoneOptions]
  );
  const initialValues = useMemo(() => mapUserToFormValues(user), [user]);

  // Кэш списков других разделов (продукты, контент, сотрудники...)
  // остаётся от старой организации — ability одного user (AbilityProvider
  // сам подхватывает новые currentOrganizationRoles/abilityRules через
  // нормализованный кэш Apollo) этого не чинит. Флаг ставится в
  // handleSubmit (до ответа мутации) и читается в onCompleted — как и в
  // useSignOut, самый надёжный способ сбросить весь стейт — reload.
  const organizationChangedRef = useRef(false);

  const [updateCurrentUser, { loading: updating }] = useMutation(
    UpdateCurrentUserMutation,
    {
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
      onCompleted: () => {
        if (organizationChangedRef.current) {
          reloadWithoutPrompt();
          return;
        }
        enqueueSnackbar(t("userSettings.snackbar.updated"), {
          variant: "success",
        });
      },
    }
  );

  const handleSubmit = (values: Record<string, unknown>) => {
    organizationChangedRef.current =
      typeof values.currentOrganizationId === "string" &&
      values.currentOrganizationId !== user.currentOrganizationId;

    updateCurrentUser({
      variables: {
        input: {
          firstName: values.firstName,
          lastName: values.lastName,
          country: values.country,
          language: values.language,
          timeZone: values.timeZone,
          currentOrganizationId: values.currentOrganizationId,
          agreeToMarketingNewsletters: values.agreeToMarketingNewsletters,
        },
      },
    });

    // Мутация только сохраняет язык на сервере — сама i18n-инстанция ни на
    // что не подписана и её никто не свапает (в отличие от референса,
    // partner/SettingsDialog.js, где смена языка UI шла тем же обработчиком).
    // Переключаем сразу же, чтобы форма не выглядела нерабочей.
    if (
      typeof values.language === "string" &&
      values.language !== i18n.language
    ) {
      i18n.changeLanguage(values.language);
    }
  };

  const handleLogout = () => {
    signOut();
  };

  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("userSettings.card.title")}
        subheader={t("userSettings.card.subheader")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <FormBuilder
          formId="user-settings-form"
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          submitButton
          resetButton={false}
          submitButtonText={t("common.actions.save")}
          disabled={updating}
        />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={8}>
            <Typography color="textSecondary" variant="body1">
              {t("userSettings.signedInAs", { email: user.email })}
            </Typography>
          </Grid>
          <Grid size={4} sx={{ textAlign: "right" }}>
            <Button color="primary" onClick={handleLogout}>
              {t("userSettings.signOut")}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
